// ============================================================================
// CONFIG SETTINGS SCREEN
// ============================================================================
// Main screen for the Configuration Settings module

import React, { useState, useCallback, useRef } from 'react';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useConfigSettings } from 'hooks/api/useConfigSettings';
import type { ScopeType } from 'hooks/api/useConfigSettings';
import ConfigGroupSidebar from './ConfigGroupSidebar';
import ConfigSettingsPanel from './ConfigSettingsPanel';

interface ConfigSettingsScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

/**
 * The template's one Settings screen: every group, the module switches first (settings
 * cleanup C1/C7). Term types (PROPERTY_TERMS) are kept in the Property Terms master that uses
 * them (C9), so they are not repeated here.
 */
export const SETTINGS_AREAS: ReadonlyArray<string | null> = [
  'MODULES', 'BOOKING', 'FRONT_OFFICE', 'NOTIFICATIONS', 'TAX_BILLING', 'APPROVALS', null,
];

const ConfigSettingsScreen: React.FC<ConfigSettingsScreenProps> = () => {
  const {
    groups,
    groupsLoading,
    activeGroupKey,
    scopeType,
    selectedPropertyId,
    selectedRatePlanId,
    effectiveValues,
    definitions,
    editedValues,
    hasChanges,
    loading,
    saving,
    selectGroup,
    changeScopeType,
    selectProperty,
    selectRatePlan,
    updateValue,
    resetValue,
    discardChanges,
    saveChanges,
  } = useConfigSettings(SETTINGS_AREAS);

  // Pending navigation when unsaved changes exist
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  // Save and discard confirmation dialogs
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  /**
   * The Approval Routing section (APPROVAL_RULES group) edits a different table
   * through its own endpoints and owns its own Save. It only reports whether it
   * holds unsaved edits, so the guards below can protect them exactly like
   * config-value edits — otherwise switching group or property would silently
   * drop a pending role change.
   */
  const [routingDirty, setRoutingDirty] = useState(false);
  const [routingDiscardSignal, setRoutingDiscardSignal] = useState(0);
  const anyChanges = hasChanges || routingDirty;

  // Active group object
  const activeGroup =
    groups.find((g) => g.groupKey === activeGroupKey) ?? null;

  // Guard group selection with unsaved changes check
  const handleGroupSelect = useCallback(
    (groupKey: string) => {
      if (groupKey === activeGroupKey) return;
      if (anyChanges) {
        pendingActionRef.current = () => selectGroup(groupKey);
        setConfirmOpen(true);
      } else {
        selectGroup(groupKey);
      }
    },
    [activeGroupKey, anyChanges, selectGroup]
  );

  // Guard scope change with unsaved changes check
  const handleScopeTypeChange = useCallback(
    (newScope: ScopeType) => {
      if (newScope === scopeType) return;
      if (anyChanges) {
        pendingActionRef.current = () => changeScopeType(newScope);
        setConfirmOpen(true);
      } else {
        changeScopeType(newScope);
      }
    },
    [scopeType, anyChanges, changeScopeType]
  );

  // Guard property change with unsaved changes check
  const handlePropertySelect = useCallback(
    (propertyId: string | null) => {
      if (propertyId === selectedPropertyId) return;
      if (anyChanges) {
        pendingActionRef.current = () => selectProperty(propertyId);
        setConfirmOpen(true);
      } else {
        selectProperty(propertyId);
      }
    },
    [selectedPropertyId, anyChanges, selectProperty]
  );

  // Guard rate plan change with unsaved changes check
  const handleRatePlanSelect = useCallback(
    (ratePlanId: string | null) => {
      if (ratePlanId === selectedRatePlanId) return;
      if (anyChanges) {
        pendingActionRef.current = () => selectRatePlan(ratePlanId);
        setConfirmOpen(true);
      } else {
        selectRatePlan(ratePlanId);
      }
    },
    [selectedRatePlanId, anyChanges, selectRatePlan]
  );

  // Confirm discard — clears config-value edits AND staged routing edits, since
  // the guard that raised this dialog was protecting both.
  const handleConfirmDiscard = useCallback(() => {
    discardChanges();
    setRoutingDiscardSignal((n) => n + 1);
    setConfirmOpen(false);
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  }, [discardChanges]);

  // Cancel discard
  const handleCancelDiscard = useCallback(() => {
    setConfirmOpen(false);
    pendingActionRef.current = null;
  }, []);

  // Save confirmation
  const handleSaveClick = useCallback(() => {
    setSaveConfirmOpen(true);
  }, []);

  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    saveChanges();
  }, [saveChanges]);

  const handleCancelSave = useCallback(() => {
    setSaveConfirmOpen(false);
  }, []);

  // Discard confirmation
  const handleDiscardClick = useCallback(() => {
    setDiscardConfirmOpen(true);
  }, []);

  const handleConfirmDiscardChanges = useCallback(() => {
    setDiscardConfirmOpen(false);
    discardChanges();
    // "Discard all unsaved changes" means all of them, routing included.
    setRoutingDiscardSignal((n) => n + 1);
  }, [discardChanges]);

  const handleCancelDiscardChanges = useCallback(() => {
    setDiscardConfirmOpen(false);
  }, []);

  return (
    <div className="module-master-container">
      <div className="config-settings-container">
        <ConfigGroupSidebar
          groups={groups}
          activeGroupKey={activeGroupKey}
          onGroupSelect={handleGroupSelect}
          loading={groupsLoading}
        />
        <ConfigSettingsPanel
          activeGroup={activeGroup}
          scopeType={scopeType}
          onScopeTypeChange={handleScopeTypeChange}
          selectedPropertyId={selectedPropertyId}
          onPropertySelect={handlePropertySelect}
          selectedRatePlanId={selectedRatePlanId}
          onRatePlanSelect={handleRatePlanSelect}
          effectiveValues={effectiveValues}
          definitions={definitions}
          editedValues={editedValues}
          onValueChange={updateValue}
          onResetValue={resetValue}
          onSave={handleSaveClick}
          onDiscard={handleDiscardClick}
          hasChanges={hasChanges}
          saving={saving}
          loading={loading}
          onRoutingDirtyChange={setRoutingDirty}
          routingDiscardSignal={routingDiscardSignal}
        />
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
        variant="danger"
      />

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save Changes"
        message="Are you sure you want to save the configuration changes?"
        confirmLabel="Save"
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={handleCancelSave}
        variant="success"
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes"
        message="Are you sure you want to discard all unsaved changes?"
        confirmLabel="Discard"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDiscardChanges}
        onCancel={handleCancelDiscardChanges}
        variant="danger"
      />
    </div>
  );
};

export default ConfigSettingsScreen;
